import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AiSearchField } from "./AiSearchField";
import { getRecentSlugs } from "./RecentlyViewedCarousel";
import { ExperienceCard } from "./ExperienceCard";
import { useUiTranslations } from "@/hooks/use-ui-translations";
import { useFeaturedExperiences } from "@/hooks/use-experiences";
import { useExperienceReviews } from "@/hooks/use-experience-reviews";
import { calculateGrossPrice, resolveMarkup } from "@/lib/pricing";
import type { ExperienceWithPricing } from "@/hooks/use-experiences";

const HERO_POSTER = "/images/hero-experiences.webp";

export function HeroSection() {
  const { t } = useUiTranslations();
  const { data: featured } = useFeaturedExperiences();
  const { data: reviewStats } = useExperienceReviews();

  const [recentSlugs, setRecentSlugs] = useState<string[]>([]);
  useEffect(() => { setRecentSlugs(getRecentSlugs()); }, []);

  const { data: recentExperiences } = useQuery({
    queryKey: ["hero-recently-viewed", recentSlugs],
    queryFn: async () => {
      if (!recentSlugs.length) return [];
      const { data, error } = await supabase
        .from("experiences")
        .select("*")
        .in("slug", recentSlugs.slice(0, 2))
        .eq("status", "active");
      if (error) throw error;

      const { data: markupRules } = await supabase
        .from("markup_rules")
        .select("provider_id, experience_id, markup_percent");
      const rules = markupRules ?? [];

      const sorted = recentSlugs
        .slice(0, 2)
        .map((s) => (data ?? []).find((e) => e.slug === s))
        .filter(Boolean) as NonNullable<typeof data>;

      return sorted.map((exp) => {
        const markup = resolveMarkup(
          rules.map((r) => ({ provider_id: r.provider_id, experience_id: r.experience_id, markup_percent: r.markup_percent })),
          exp.provider_id,
          exp.id
        );
        return {
          ...exp,
          grossPricePerPerson: exp.net_price_per_person ? calculateGrossPrice(exp.net_price_per_person, markup) : null,
          grossPricePerGroup: exp.net_price_per_group ? calculateGrossPrice(exp.net_price_per_group, markup) : null,
          markupPercent: markup,
          dynamicModifierPercent: 0,
        } as ExperienceWithPricing;
      });
    },
    enabled: recentSlugs.length > 0,
    staleTime: 60_000,
  });

  // Build 2 puff slots: replace from the left with recent visits, fill remainder with featured
  const recentCount = recentExperiences?.length ?? 0;
  const featuredList = featured ?? [];

  const puffs: ExperienceWithPricing[] = [];
  // Slot 0
  if (recentCount >= 1) {
    puffs.push(recentExperiences![0]);
  } else if (featuredList.length > 0) {
    puffs.push(featuredList[0]);
  }
  // Slot 1
  if (recentCount >= 2) {
    puffs.push(recentExperiences![1]);
  } else if (featuredList.length > (recentCount >= 1 ? 0 : 1)) {
    // Pick the first featured that isn't already used
    const usedIds = new Set(puffs.map((p) => p.id));
    const next = featuredList.find((f) => !usedIds.has(f.id));
    if (next) puffs.push(next);
  }

  const isReturning = recentCount > 0;

  return (
    <section className="relative flex min-h-[70vh] items-center justify-center md:min-h-screen overflow-visible">
      <video
        autoPlay
        muted
        loop
        playsInline
        // @ts-ignore — fetchpriority is valid HTML but not in React's types yet
        fetchpriority="high"
        poster={HERO_POSTER}
        className="absolute inset-0 h-full w-full object-cover"
      >
        <source src="https://www.swedishlapland.com/wp-content/uploads/Winter-Banner_1.mp4" type="video/mp4" />
      </video>

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />

      <div className="relative z-30 mx-auto max-w-3xl px-6 text-center">
        <h1 className="font-sans text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-7xl">
          {t("hero.headline")}
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-base font-normal leading-relaxed text-white/85 sm:text-lg md:text-xl">
          {t("hero.subline")}
        </p>

        <div className="mt-10">
          <AiSearchField />
        </div>
      </div>

      {/* Overlapping experience puff cards */}
      {puffs.length > 0 && (
        <div className="absolute -bottom-36 sm:-bottom-32 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 px-4 w-full max-w-sm sm:max-w-3xl">
          {isReturning && (
            <p className="text-sm font-medium tracking-wide text-white/80 italic">
              {t("hero.returningHeadline") || "Pick up where you left off"}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full">
            {puffs.map((exp) => (
              <ExperienceCard
                key={exp.id}
                experience={exp}
                compact
                reviewScore={reviewStats?.[exp.id] ?? null}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
